import type { AuthResponse } from "@epos/contracts";
import { type FormEvent, useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { api, tokenStore } from "../api";
import { useAuth } from "./AuthContext";
import { TwoFactorGate } from "./TwoFactorGate";

const rememberedPosEmailKey = "epos-bookings-pos-account";

export function PosLoginPage({ subdomain }: { subdomain: string }) {
  const { user } = useAuth();
  const desktop = Boolean(window.eposDesktop);
  const rememberedEmail = localStorage.getItem(rememberedPosEmailKey) ?? "";
  const [email, setEmail] = useState(rememberedEmail);
  const [password, setPassword] = useState("");
  const [remember, setRemember] = useState(desktop || Boolean(rememberedEmail));
  const [pending, setPending] = useState(false);
  const [error, setError] = useState("");
  const [twoFactor, setTwoFactor] = useState<AuthResponse>();

  useEffect(() => {
    if (!desktop) return;
    window.eposDesktop?.credentials.load().then(saved => {
      if (!saved) return;
      setEmail(saved.email);
      setPassword(saved.password);
      setRemember(true);
    }).catch(() => undefined);
  }, [desktop]);

  if (user) return <Navigate to="/pos" replace />;
  const finish = (response: AuthResponse) => {
    tokenStore.set(response.token);
    window.location.assign("/pos");
  };
  const submit = async (event: FormEvent) => {
    event.preventDefault(); setPending(true); setError("");
    try {
      // Remembering an account and trusting a device are independent choices.
      // Remembering must not force MFA or suppress the trusted-device cookie.
      const response = await api.auth.posLogin(subdomain, { email, password });
      if (remember) localStorage.setItem(rememberedPosEmailKey, email.trim());
      else localStorage.removeItem(rememberedPosEmailKey);
      if (desktop) {
        if (remember) await window.eposDesktop!.credentials.save({ email: email.trim(), password });
        else await window.eposDesktop!.credentials.clear();
      }
      if ((response.authenticationStage ?? "complete") === "complete") finish(response);
      else setTwoFactor(response);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "OPPS! Login is required");
    } finally { setPending(false); }
  };

  return <main className="pos-login-replica"><div className="pos-login-container">
    <img className="pos-login-logo" src="/pos/images/logo-white.svg" alt="Epos Bookings" />
    <section className="pos-login-box"><h1>Login to POS</h1>
      {twoFactor
        ? <TwoFactorGate response={twoFactor} complete={finish} cancel={() => setTwoFactor(undefined)} />
        : <><form onSubmit={submit}>
          <label><i className="fas fa-user" /><input aria-label="User Id" required type="email" autoComplete="username" placeholder="name@example.com" value={email} onChange={event => setEmail(event.target.value)} /><span>User Id</span></label>
          <label><i className="fas fa-lock" /><input aria-label="Password" required type="password" autoComplete="current-password" placeholder="password" value={password} onChange={event => setPassword(event.target.value)} /><span>Password</span></label>
          <label className="pos-remember"><input type="checkbox" checked={remember} onChange={event => setRemember(event.target.checked)} /> Remember this account on the POS</label>
          {remember && <p className="pos-login-security-note">{desktop ? "Your username and password are protected by Windows encryption on this device. If you trust this device during verification, the second step is skipped for the configured trust period." : "Only the email address is remembered. Your password can be held by the browser password manager, and you can separately trust this device during verification."}</p>}
          {error && <p className="pos-login-error">{error}</p>}
          <button disabled={pending}>{pending ? "Signing in…" : "Login"}</button>
        </form><Link to="/forgot-password">Recover Password</Link></>}
    </section>
  </div></main>;
}
